import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiError, listCapas } from "../services/AiService";
import { CAPA_FLOW, CapaListItem, CapaStatus } from "../types/ai";
import { CapaChip, ClassificationChip, ErrorAlert, Loading, PageShell, RiskChip, fmtDay } from "../component/aces/Shared";

const CapaListScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<CapaListItem[] | null>(null);
  const [status, setStatus] = useState<string>("");
  const [overdue, setOverdue] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [mine, setMine] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setItems(null);
    listCapas({ status: status || undefined, overdue: overdue || undefined, urgent: urgent || undefined, mine: mine || undefined })
      .then(setItems)
      .catch((e) => {
        setError(apiError(e));
        setItems([]);
      });
  }, [status, overdue, urgent, mine]);

  const statuses: CapaStatus[] = [...CAPA_FLOW, "REJECTED", "REOPENED"];
  return (
    <PageShell title={t("aces.capa.registerTitle")}>
      <ErrorAlert message={error} onClose={() => setError(null)} />
      <div className="aces-actions mb-3">
        <select className="form-control" style={{ maxWidth: 260 }} aria-label={t("aces.capa.status")} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t("aces.capa.allStatuses")}</option>
          {statuses.map((s) => <option key={s} value={s}>{t(`aces.enum.capa.${s}`)}</option>)}
        </select>
        {([["overdue", overdue, setOverdue], ["urgent", urgent, setUrgent], ["mine", mine, setMine]] as [string, boolean, (v: boolean) => void][]).map(([k, v, set]) => (
          <label key={k} className="form-check-label" style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" className="form-check-input" checked={v} onChange={(e) => set(e.target.checked)} /> {t(`aces.capa.filter.${k}`)}
          </label>
        ))}
      </div>
      {items === null ? <Loading /> : (
        <div className="__defaultTable aces-table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("aces.capa.ref")}</th>
                <th>{t("aces.finding.ref")}</th>
                <th>{t("aces.finding.classification")}</th>
                <th>{t("aces.finding.risk")}</th>
                <th>{t("aces.capa.status")}</th>
                <th>{t("aces.capa.responsible")}</th>
                <th>{t("aces.capa.targetDate")}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={7}>{t("aces.capa.none")}</td></tr>
              ) : items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <button type="button" className="aces-table-link" onClick={() => navigate(`/capa/${c.id}`)}>{c.capaRef}</button>
                    {c.isUrgent && <div><span className="aces-chip darkred">{t("aces.register.critical")}</span></div>}
                  </td>
                  <td>{c.findingRef} <span className="aces-muted">{c.checklistRef}</span></td>
                  <td>{c.classification && <ClassificationChip value={c.classification} />}</td>
                  <td>{c.riskLevel && <RiskChip level={c.riskLevel} />}</td>
                  <td><CapaChip value={c.status} /></td>
                  <td>{[c.responsibleFunction, c.responsiblePerson?.name].filter(Boolean).join(" · ") || "—"}</td>
                  <td style={{ color: c.overdue ? "#b71c1c" : undefined, fontWeight: c.overdue ? 700 : undefined }}>
                    {fmtDay(c.targetDate)} {c.overdue && `(${t("aces.capa.overdue")})`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
};

export default CapaListScreen;
